import React, { useEffect, useMemo, useRef } from 'react'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { NumberField, Select, TabButtonGroup } from 'src/Libs/inputs/react-hook-form'
import { DeviceLinkModel } from 'src/Libs/installations'
import { useEm360ModbusFormInitializer } from 'src/Libs/installations/hooks'
import { emcConfigKey } from 'src/Libs/flow/installation/hooks'
import { EmcConfigDto, updateEmcConfig } from 'src/Libs/flow/installation/api'
import { usePortalMutation } from 'src/Libs/api'
import { EM360_DEFAULT_MODBUS_SETTINGS } from 'src/Libs/installations/constants'

interface Props {
  installationId?: number
  emcDeviceLink?: DeviceLinkModel
  emcConfig?: Nullable<EmcConfigDto> | null
  isEmcConfigLoading: boolean
  isEmcOnline: boolean
  isEmcConnectionLoading: boolean
  isEmcConfigError?: boolean
  emcIpAddress?: string
  hasEmcConnectionData: boolean
}

const ModbusTcpPanel: React.FC<Props> = ({
  installationId,
  emcDeviceLink,
  emcConfig,
  isEmcConfigLoading,
  isEmcOnline,
  isEmcConnectionLoading,
  emcIpAddress,
  isEmcConfigError,
  hasEmcConnectionData,
}) => {
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()

  const emcId = emcDeviceLink?.deviceId ?? null
  const deviceLinkId = emcDeviceLink?.id?.toString() ?? ''
  const { initialValues, validationSchema } = useEm360ModbusFormInitializer(emcConfig ?? undefined)

  const resolver = useMemo(() => zodResolver(validationSchema), [validationSchema])

  const methods = useForm({
    defaultValues: initialValues,
    resolver,
    mode: 'onChange',
  })
  const hasInitializedDefaults = useRef(false)

  useEffect(() => {
    methods.reset(initialValues)
  }, [methods, initialValues])

  useEffect(() => {
    hasInitializedDefaults.current = false
  }, [installationId, deviceLinkId])

  const modbusEnabled = useWatch({ control: methods.control, name: 'modbusEnabled' })

  const { mutate: saveModbusSettings, isPending: isSaving } = usePortalMutation({
    mutationFn: async (values: { modbusEnabled: boolean; modbusProtocol: 'e3dc' | 'enerfin' | 'sunspec'; modbusPort: number }) => {
      if (!installationId || !emcId) {
        throw new Error('missing-installation-or-emc')
      }

      const result = await updateEmcConfig(
        installationId,
        deviceLinkId,
        {
          useCT: emcConfig?.useCT ?? EM360_DEFAULT_MODBUS_SETTINGS.useCT,
          ratioCT: emcConfig?.ratioCT ?? EM360_DEFAULT_MODBUS_SETTINGS.ratioCT,
          modbusEnabled: values.modbusEnabled,
          modbusProtocol: values.modbusProtocol,
          modbusPort: values.modbusPort,
        },
        authFetch
      )

      if (result.hasErrors) {
        throw result.error ?? new Error('failed-to-save-modbus-settings')
      }
      return result
    },
    onSuccess: () => {
      if (installationId) {
        queryClient.invalidateQueries({ queryKey: emcConfigKey(installationId, deviceLinkId) })
      }

      if (!modbusEnabled || !hasEmcConnectionData || !isEmcOnline) {
        enqueueSuccessStack(t('installation-configuration.em360.modbus-tcp.save.success'))
        return
      }

      enqueueSuccessStack(t('installation-configuration.em360.modbus-tcp.save.success-transmitted'))
    },
    onError: error => {
      if (modbusEnabled && hasEmcConnectionData && isEmcOnline) {
        enqueueErrorStack(t('installation-configuration.em360.modbus-tcp.save.error-transmit'))
      } else {
        enqueueErrorStack(t('installation-configuration.em360.modbus-tcp.save.error'))
      }
      console.error('could not save modbus tcp settings', error)
    },
  })

  useEffect(() => {
    const hasModbusSettings = emcConfig?.modbusEnabled != null && emcConfig.modbusProtocol != null && emcConfig.modbusPort != null

    if (!installationId || !emcId || isEmcConfigLoading || isEmcConfigError || hasModbusSettings || isSaving || hasInitializedDefaults.current) {
      return
    }

    hasInitializedDefaults.current = true
    saveModbusSettings(initialValues)
  }, [emcConfig, emcId, installationId, initialValues, isEmcConfigError, isEmcConfigLoading, isSaving, saveModbusSettings])

  const isLoading = isEmcConfigLoading || isEmcConnectionLoading
  const isDisabled = !installationId || !emcId || isLoading || isSaving || !!isEmcConfigError

  const onSubmit = (values: { modbusEnabled: boolean; modbusProtocol: 'e3dc' | 'enerfin' | 'sunspec'; modbusPort: number }): void => {
    if (isDisabled) {
      return
    }

    saveModbusSettings(values)
  }

  return (
    <AccordionItem title={t('installation-configuration.em360.modbus-tcp.title')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <FormProvider {...methods}>
        <Box component={'form'} onSubmit={methods.handleSubmit(onSubmit)}>
          {isLoading ? (
            <Stack alignItems={'center'} justifyContent={'center'} sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <Stack gap={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent={'space-between'}
                alignItems={{ xs: 'flex-start', md: 'flex-start' }}
                gap={2}
              >
                <Stack gap={2} sx={{ maxWidth: 760 }}>
                  <Typography variant={'body2'}>{t('installation-configuration.em360.modbus-tcp.interface-title')}</Typography>
                  <Typography variant={'body2'}>{t('installation-configuration.em360.modbus-tcp.interface-description')}</Typography>
                </Stack>

                <Box
                  sx={{
                    width: { xs: '100%', md: 280 },
                    borderRadius: theme => theme.shape.customBorderRadius.tabButton,
                  }}
                >
                  <TabButtonGroup
                    name={'modbusEnabled'}
                    options={[
                      { value: true, label: t('installation-configuration.em360.modbus-tcp.enabled') },
                      { value: false, label: t('installation-configuration.em360.modbus-tcp.disabled') },
                    ]}
                    disabled={isDisabled}
                    styledContainerSx={{ backgroundColor: 'shades.shade04' }}
                  />
                </Box>
              </Stack>

              {modbusEnabled && (
                <Stack
                  gap={2}
                  sx={{
                    border: '1px solid',
                    borderColor: 'shades.shade03',
                    backgroundColor: 'shades.shade04',
                    borderRadius: 2,
                    p: { xs: 2, md: 3 },
                  }}
                >
                  <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
                    <Typography variant={'body2'}>{t('installation-configuration.em360.modbus-tcp.ip-address')}</Typography>
                    <Typography variant={'body2'} sx={{ textAlign: 'right' }}>
                      {emcIpAddress ?? t('installation-configuration.em360.modbus-tcp.ip-not-connected')}
                    </Typography>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent={'space-between'}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    gap={2}
                  >
                    <Typography variant={'body2'}>{t('installation-configuration.em360.modbus-tcp.protocol')}</Typography>
                    <Box sx={{ width: { xs: '100%', md: 240 } }}>
                      <Select
                        name={'modbusProtocol'}
                        label={t('installation-configuration.em360.modbus-tcp.protocol')}
                        options={[
                          { value: 'e3dc', label: 'E3/DC' },
                          { value: 'sunspec', label: 'SunSpec' },
                          { value: 'enerfin', label: 'Enerfin' },
                        ]}
                        disabled={isDisabled}
                        fullWidth
                      />
                    </Box>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent={'space-between'}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    gap={2}
                  >
                    <Typography variant={'body2'}>{t('installation-configuration.em360.modbus-tcp.port')}</Typography>
                    <Box sx={{ width: { xs: '100%', md: 240 } }}>
                      <NumberField
                        name={'modbusPort'}
                        label={t('installation-configuration.em360.modbus-tcp.port')}
                        disabled={isDisabled}
                        fullWidth
                        inputProps={{ min: 0, max: 65535 }}
                      />
                    </Box>
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}

          <Divider sx={{ marginX: -2, borderColor: 'grey.grey06', marginY: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type={'submit'} variant={'contained'} disabled={isDisabled}>
              {t('common.save')}
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </AccordionItem>
  )
}

export default ModbusTcpPanel
